/**
 * AP-PO-01: Purchase Order Workspace
 * Displays all POs with status filter, creation, and navigation to detail.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Search,
  ChevronRight,
  RefreshCw,
  Building2,
  CalendarDays,
  DollarSign,
  CheckCircle2,
  Clock,
  XCircle,
  Package,
  AlertCircle,
  Archive,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  DRAFT: { label: "Draft", color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: FileText },
  PENDING_APPROVAL: { label: "Pending Approval", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  APPROVED: { label: "Approved", color: "bg-blue-100 text-blue-800 border-blue-200", icon: CheckCircle2 },
  ISSUED: { label: "Issued", color: "bg-green-100 text-green-800 border-green-200", icon: Package },
  CLOSED: { label: "Closed", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Archive },
  CANCELLED: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
};

function formatAmount(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-700", icon: AlertCircle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ── Create PO Dialog ──────────────────────────────────────────────────────────

function CreatePODialog({ open, onClose, onCreated }) {
  const [suppliers, setSuppliers] = useState([]);
  const [events, setEvents] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    supplier_id: "",
    event_master_id: "",
    currency_code: "INR",
    notes: "",
  });
  const [lines, setLines] = useState([
    { description: "", quantity: "1", unit: "unit", unit_rate_minor: "", tax_code_id: "" },
  ]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      axios.get(`${API}/api/ueim02/suppliers/?limit=100`, { withCredentials: true }),
      axios.get(`${API}/api/ueim04/events/?limit=100`, { withCredentials: true }),
      axios.get(`${API}/api/finance/tax-codes`, { withCredentials: true }),
    ]).then(([sRes, eRes, tRes]) => {
      const supp = sRes.data?.results || sRes.data || [];
      setSuppliers(supp.filter((s) => s.status === "APPROVED"));
      const evts = eRes.data?.event_masters || eRes.data?.events || eRes.data?.results || eRes.data || [];
      setEvents(Array.isArray(evts) ? evts : []);
      setTaxCodes(Array.isArray(tRes.data) ? tRes.data : []);
    }).catch(() => {});
  }, [open]);

  const addLine = () =>
    setLines((l) => [...l, { description: "", quantity: "1", unit: "unit", unit_rate_minor: "", tax_code_id: "" }]);

  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const updateLine = (i, field, val) =>
    setLines((l) => l.map((line, idx) => (idx === i ? { ...line, [field]: val } : line)));

  const lineTotal = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const rate = parseFloat(line.unit_rate_minor) || 0;
    const sub = qty * rate;
    const tc = taxCodes.find((t) => t.id === line.tax_code_id && line.tax_code_id !== "NONE");
    const tax = tc ? sub * (tc.rate_bps / 10000) : 0;
    return sub + tax;
  };

  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const handleSubmit = async () => {
    if (!form.supplier_id || !form.event_master_id) {
      toast.error("Supplier and Event are required");
      return;
    }
    const validLines = lines.filter((l) => l.description && l.unit_rate_minor);
    if (!validLines.length) {
      toast.error("At least one complete line item is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        lines: validLines.map((l) => ({
          description: l.description,
          quantity: String(l.quantity),
          unit: l.unit,
          unit_rate_minor: Math.round(parseFloat(l.unit_rate_minor) * 100), // convert ₹ to paise
          tax_code_id: l.tax_code_id && l.tax_code_id !== "NONE" ? l.tax_code_id : undefined,
        })),
      };
      const res = await axios.post(`${API}/api/ap/purchase-orders`, payload, { withCredentials: true });
      toast.success("Purchase Order created successfully");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create Purchase Order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Create Purchase Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Header fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600">Supplier *</label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="po-supplier-select">
                  <SelectValue placeholder="Select approved supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.display_name || s.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600">Event *</label>
              <Select value={form.event_master_id} onValueChange={(v) => setForm({ ...form, event_master_id: v })}>
                <SelectTrigger data-testid="po-event-select">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((e) => (
                    <SelectItem key={e.id || e._id} value={e.id || e._id}>
                      {e.event_name || e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-zinc-600">Internal Notes</label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional internal notes"
              data-testid="po-notes-input"
            />
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-zinc-800">Line Items</span>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line-btn">
                <Plus size={13} className="mr-1" /> Add Line
              </Button>
            </div>
            <div className="space-y-3">
              {lines.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                  <div className="col-span-5 space-y-1">
                    <label className="text-xs text-zinc-500">Description *</label>
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(i, "description", e.target.value)}
                      placeholder="Service description"
                      className="text-sm"
                      data-testid={`po-line-desc-${i}`}
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs text-zinc-500">Qty</label>
                    <Input
                      type="number"
                      value={line.quantity}
                      onChange={(e) => updateLine(i, "quantity", e.target.value)}
                      className="text-sm"
                      data-testid={`po-line-qty-${i}`}
                    />
                  </div>
                  <div className="col-span-3 space-y-1">
                    <label className="text-xs text-zinc-500">Rate (₹) *</label>
                    <Input
                      type="number"
                      value={line.unit_rate_minor}
                      onChange={(e) => updateLine(i, "unit_rate_minor", e.target.value)}
                      placeholder="0.00"
                      className="text-sm"
                      data-testid={`po-line-rate-${i}`}
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs text-zinc-500">Tax</label>
                    <Select value={line.tax_code_id} onValueChange={(v) => updateLine(i, "tax_code_id", v)}>
                      <SelectTrigger className="text-xs">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">No Tax</SelectItem>
                        {taxCodes.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {lines.length > 1 && (
                    <div className="col-span-12 flex items-center justify-between pt-1">
                      <span className="text-xs text-zinc-500">
                        Line total: <strong>{formatAmount(lineTotal(line))}</strong>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-red-500 hover:text-red-700 text-xs"
                        onClick={() => removeLine(i)}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <div className="text-sm font-semibold text-zinc-800">
                Grand Total:{" "}
                <span className="text-blue-700 text-base">{formatAmount(grandTotal)}</span>
                <span className="text-xs text-zinc-400 font-normal ml-1">(incl. tax)</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="po-create-submit-btn">
            {loading ? "Creating..." : "Create Draft PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main PO Workspace ─────────────────────────────────────────────────────────

export default function PurchaseOrderWorkspace() {
  const navigate = useNavigate();
  const [pos, setPOs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showCreate, setShowCreate] = useState(false);

  const fetchPOs = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", skip: "0" });
      if (status && status !== "ALL") params.set("status", status);
      const res = await axios.get(`${API}/api/ap/purchase-orders?${params}`, { withCredentials: true });
      setPOs(res.data?.results || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error("Failed to load Purchase Orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPOs(statusFilter);
  }, [statusFilter, fetchPOs]);

  const filtered = searchTerm
    ? pos.filter(
        (p) =>
          p.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.event_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : pos;

  const handleCreated = () => fetchPOs(statusFilter);

  const kpis = [
    { label: "Total POs", value: total, icon: FileText, color: "text-blue-600" },
    { label: "Issued", value: pos.filter((p) => p.status === "ISSUED").length, icon: Package, color: "text-green-600" },
    { label: "Pending Approval", value: pos.filter((p) => p.status === "PENDING_APPROVAL").length, icon: Clock, color: "text-amber-600" },
    { label: "Draft", value: pos.filter((p) => p.status === "DRAFT").length, icon: FileText, color: "text-zinc-500" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="po-workspace">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Purchase Orders</h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Manage supplier POs across all events
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-po-btn"
          >
            <Plus size={15} className="mr-1.5" />
            Create PO
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-4 gap-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white rounded-xl border border-zinc-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-medium uppercase tracking-wide">{kpi.label}</span>
                  <Icon size={16} className={kpi.color} />
                </div>
                <p className={`text-2xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search PO number, supplier, event…"
              className="pl-9"
              data-testid="po-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="po-status-filter">
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => fetchPOs(statusFilter)} title="Refresh">
            <RefreshCw size={15} />
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-zinc-400">
              <RefreshCw size={18} className="animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-zinc-400 space-y-2">
              <Package size={32} className="opacity-30" />
              <p className="text-sm">No Purchase Orders found</p>
              <Button variant="outline" size="sm" onClick={() => setShowCreate(true)}>
                <Plus size={13} className="mr-1" /> Create your first PO
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="po-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">PO Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Event</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Amount</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((po) => (
                  <tr
                    key={po.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/finance/purchase-orders/${po.id}`)}
                    data-testid={`po-row-${po.id}`}
                  >
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-zinc-900 font-mono text-xs">
                        {po.po_number || <span className="text-zinc-400 font-sans">Draft — {po.id.slice(0, 8)}</span>}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {new Date(po.created_at).toLocaleDateString("en-IN")}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Building2 size={13} className="text-zinc-400 flex-shrink-0" />
                        <span className="text-zinc-700 truncate max-w-[160px]">{po.supplier_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <CalendarDays size={13} className="text-zinc-400 flex-shrink-0" />
                        <span className="text-zinc-600 truncate max-w-[160px]">{po.event_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-zinc-800">
                      {formatAmount(po.total_minor, po.currency_code)}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <ChevronRight size={15} className="text-zinc-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CreatePODialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
